import { useState, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./rainbow.css"; // Custom CSS for elements that need special styling
import { Link } from "react-router-dom";
import { Howl } from "howler";
import { useAudio } from "../../../../Providers/AudioProvider";
import useUserInfo from "../../../../Hooks/useUserInfo";
import { userGameRewards } from "../../../../apis/user";
import { logPageView } from "../../../../components/analytics";
import task from "../../Task";

// Sound effects
const soundEffects = {
  pop: new Howl({
    src: ["/sounds/pop.mp3"],
    volume: 0.5,
  }),
  connect: new Howl({
    src: ["/sounds/connect.mp3"],
    volume: 0.3,
  }),
  combo: new Howl({
    src: ["/sounds/combo.mp3"],
    volume: 0.4,
  }),
  gameOver: new Howl({
    src: ["/sounds/game-over.mp3"],
    volume: 0.6,
  }),
};

// The main component for the Rainbow Ball Connect game
export default function RainbowBallConnectGame() {
  const ROWS = 8;
  const COLS = 8;
  const COLORS = [
    { name: "red", gradient: "radial-gradient(circle, #ff6b6b, #c44d4d)" },
    { name: "blue", gradient: "radial-gradient(circle, #6ab7ff, #4472c4)" },
    { name: "green", gradient: "radial-gradient(circle, #7ae582, #4caf50)" },
    { name: "yellow", gradient: "radial-gradient(circle, #ffeb3b, #ffc107)" },
    { name: "purple", gradient: "radial-gradient(circle, #ba68c8, #8e24aa)" },
    { name: "orange", gradient: "radial-gradient(circle, #ffa726, #f57c00)" },
  ];

  // Game state
  const [board, setBoard] = useState([]);
  const [visualBoard, setVisualBoard] = useState([]);
  const [selected, setSelected] = useState(null);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [IsGameOver, setIsGameOver] = useState(false); // New state for API game over
  const [moves, setMoves] = useState(20);
  const [connecting, setConnecting] = useState(false);
  const [lastConnected, setLastConnected] = useState(null);
  const [comboCount, setComboCount] = useState(0);
  const [showCombo, setShowCombo] = useState(false);
  const [swipeChain, setSwipeChain] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [highlightPath, setHighlightPath] = useState([]);
  const [animatingFall, setAnimatingFall] = useState(false);
  const [animatingPop, setAnimatingPop] = useState([]);
  const [pointsAnimations, setPointsAnimations] = useState([]);
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  const [showIntroText, setShowIntroText] = useState(true); // New state for intro text
  const [introTextFading, setIntroTextFading] = useState(false);

  // User info and API
  const { userDetails, updateUserInfo, updatewatchScreenInfo, watchScreen } =
    useUserInfo();
  const playClick = useAudio();
  const IsPointsReached =
    watchScreen?.LoginMessage ===
    "You have already reached the maximum rewards limit"
      ? true
      : false;

  // Refs
  const gameBoardRef = useRef(null);
  const ballPositions = useRef(new Map());
  const gameContainerRef = useRef(null);

  // Log page view
  useEffect(() => {
    logPageView("Rainbow Connect", "Rainbow Connect Game");
  }, []);

  // Handle API response
  const handleApiResponse = async (points) => {
    updateUserInfo((prev) => ({
      ...prev,
      isLoading: true,
    }));
    try {
      const apiData = {
        gamePoints: points,
        gameType: "RainbowConnect",
      };
      const res = await userGameRewards(apiData);
      if (res) {
        updateUserInfo((prev) => ({
          ...prev,
          isLoading: false,
        }));
        updatewatchScreenInfo((prev) => ({
          ...prev,
          LoginMessage: res?.message,
        }));
        setIsGameOver(true);
      }
    } catch (error) {
      console.error("API call failed:", error);
      updateUserInfo((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  // Toggle menu function
  const toogleMenu = () => {
    playClick();
    updateUserInfo((prev) => ({
      ...prev,
      isPlay: false,
      currentComponent: task,
      currentComponentText: "task",
      lastComponent: userDetails?.userDetails.currentComponent,
      lastComponentText: userDetails?.userDetails.currentComponentText,
      isMenu: true,
      menuCount: userDetails?.userDetails?.menuCount + 1,
    }));
  };

  // Close popup
  const closePopUp = () => {
    playClick();
    setIsGameOver(false);
    setGameOver(false);
  };

  // Add meta viewport and prevent iOS gestures
  useEffect(() => {
    // Add meta viewport tag to prevent zooming
    const viewportMeta = document.createElement("meta");
    viewportMeta.name = "viewport";
    viewportMeta.content =
      "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no";
    document.head.appendChild(viewportMeta);

    // Prevent Telegram vertical swipe behavior
    if (window.Telegram && window.Telegram.WebApp) {
      // Using the proper WebApp API method to disable vertical swipes
      // This prevents the app from collapsing when swiping inside the game area
      if (typeof window.Telegram.WebApp.disableVerticalSwipes === "function") {
        window.Telegram.WebApp.disableVerticalSwipes();
      } else {
        // Fallback for older versions that don't have the disableVerticalSwipes method
        const data = JSON.stringify({
          eventType: "web_app_setup_swipe_behavior",
          eventData: {
            allow_vertical_swipe: false,
          },
        });

        // Handle different Telegram environments
        if (window.TelegramWebviewProxy) {
          // Desktop and Mobile
          window.TelegramWebviewProxy.postEvent(
            "web_app_setup_swipe_behavior",
            JSON.stringify({ allow_vertical_swipe: false })
          );
        } else if (window.external && window.external.notify) {
          // Windows Phone
          window.external.notify(data);
        } else {
          // Web version
          window.parent.postMessage(data, "*");
        }
      }
    }

    // Prevent iOS-specific gestures that cause Telegram mini app to minimize
    const preventDefaultForTouch = (e) => {
      // Check if we're currently dragging in the game - if so, prevent all default behaviors
      if (isDragging) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Don't prevent default for buttons and interactive elements
      if (
        e.target.tagName === "BUTTON" ||
        e.target.closest("button") ||
        e.target.role === "button" ||
        e.target.className.includes("btn") ||
        e.target.onclick
      ) {
        return; // Allow buttons to work
      }

      // Prevent default for game board elements
      if (
        gameBoardRef.current &&
        (gameBoardRef.current.contains(e.target) ||
          e.target === gameBoardRef.current)
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // Add event listeners with passive: false to ensure preventDefault works
    document.addEventListener("touchstart", preventDefaultForTouch, {
      passive: false,
    });
    document.addEventListener("touchmove", preventDefaultForTouch, {
      passive: false,
    });
    document.addEventListener("touchend", preventDefaultForTouch, {
      passive: false,
    });

    // Add specific iOS body styling to prevent bouncing/scrolling
    document.body.style.overscrollBehavior = "none";
    document.body.style.overflow = "hidden";

    // Clean up when component unmounts
    return () => {
      document.head.removeChild(viewportMeta);
      document.removeEventListener("touchstart", preventDefaultForTouch);
      document.removeEventListener("touchmove", preventDefaultForTouch);
      document.removeEventListener("touchend", preventDefaultForTouch);
      document.body.style.overscrollBehavior = "";
      document.body.style.overflow = "";
    };
  }, [isDragging]);

  // Initialize the game board
  useEffect(() => {
    resetGame();
  }, []);

  // Call API when game is over
  useEffect(() => {
    if (gameOver && !IsGameOver) {
      handleApiResponse(score);
    }
  }, [gameOver]);

  // Sync logical board to visual board with animations
  useEffect(() => {
    if (!animatingFall) {
      setVisualBoard(structuredClone(board));
    }
  }, [board, animatingFall]);

  // Clean up point animations after they finish
  useEffect(() => {
    if (pointsAnimations.length > 0) {
      const timer = setTimeout(() => {
        setPointsAnimations([]);
      }, 1600); // Slightly longer than the animation duration (1.5s)

      return () => clearTimeout(timer);
    }
  }, [pointsAnimations]);

  // Show intro text only on first visit, not on every game reset
  useEffect(() => {
    // Check if we've already shown the intro this session
    const hasSeenIntro = sessionStorage.getItem("rainbowIntroSeen") === "true";

    if (!hasSeenIntro) {
      setShowIntroText(true);
    } else {
      setShowIntroText(false);
    }
  }, []);

  // Auto-hide intro text after a few seconds
  useEffect(() => {
    if (showIntroText) {
      const timer = setTimeout(() => {
        handleHideIntroText();
      }, 5000); // Auto-hide after 5 seconds

      return () => clearTimeout(timer);
    }
  }, [showIntroText]);

  // Handle fading out the intro text
  const handleHideIntroText = () => {
    if (showIntroText && !introTextFading) {
      setIntroTextFading(true);

      // Mark that user has seen the intro this session
      sessionStorage.setItem("rainbowIntroSeen", "true");

      // Wait for fade animation to complete before removing from DOM
      setTimeout(() => {
        setShowIntroText(false);
        setIntroTextFading(false);
      }, 500); // Match the CSS transition duration
    }
  };

  // Add handler to dismiss intro text when clicking anywhere on the game board
  const dismissIntroText = () => {
    if (showIntroText) {
      handleHideIntroText();
    }
  };

  // Handle ball down starts a game if it hasn't started yet
  const handleBallDown = (ball, e, element) => {
    if (gameOver || connecting || animatingFall) return;
    e.preventDefault();
    e.stopPropagation();

    // Hide intro text if it's showing
    handleHideIntroText();

    registerBallPosition(ball, element);
    setIsDragging(true);
    setSwipeChain([ball]);
    setHighlightPath([{ row: ball.row, col: ball.col }]);
    setSelected(null); // Clear any previous selection

    // Play connect sound
    playSound("connect");
  };

  // Handle mouse/touch move
  const handleBallMove = (ball, e, element) => {
    if (!isDragging || gameOver || connecting || animatingFall || !ball) return;
    e.preventDefault();
    e.stopPropagation();

    registerBallPosition(ball, element);

    // Check if the ball is adjacent to any ball in the chain
    const isAdjacentToChain = swipeChain.some((chainBall) =>
      areAdjacent(ball, chainBall)
    );

    if (
      isAdjacentToChain &&
      ball.color.name === swipeChain[0].color.name &&
      !isInChain(ball)
    ) {
      const newChain = [...swipeChain, ball];
      setSwipeChain(newChain);
      setHighlightPath([...highlightPath, { row: ball.row, col: ball.col }]);

      // Visual effect when connecting
      const pos = getBallPosition(ball.row, ball.col);
      if (pos && gameBoardRef.current) {
        playSound("connect");
      }
    }
  };

  const scrollableRef = useRef(null);

  useEffect(() => {
    const preventTouchScroll = (event) => {
      // Allow scrolling only for the scrollable div
      if (
        scrollableRef.current &&
        !scrollableRef.current.contains(event.target)
      ) {
        event.preventDefault();
      }
    };
    // Add the event listener
    document.addEventListener("touchmove", preventTouchScroll, {
      passive: false,
    });
    // Cleanup the event listener
    return () => {
      document.removeEventListener("touchmove", preventTouchScroll);
    };
  }, []);

  // Handle mouse/touch up - complete the swipe
  const handleBallUp = (e) => {
    if (!isDragging || gameOver || connecting || animatingFall) return;
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setIsDragging(false);

    // Process the chain if it has at least 2 balls
    if (swipeChain.length > 1) {
      const newBoard = [...board];
      const newVisualBoard = [...visualBoard];
      let points = swipeChain.length * 5; // 5 points per ball

      // Calculate center position for points animation
      let centerRow = 0,
        centerCol = 0;
      const popPositions = [];

      swipeChain.forEach((ball) => {
        centerRow += ball.row;
        centerCol += ball.col;

        // Mark for pop animation first
        popPositions.push({ row: ball.row, col: ball.col });
      });

      // Set the pop animation balls
      setAnimatingPop(popPositions);

      // Play pop sound
      playSound("pop");

      // After a short delay to show the pop animation, remove balls and update board
      setTimeout(() => {
        swipeChain.forEach((ball) => {
          newBoard[ball.row][ball.col] = null; // Remove connected balls
          newVisualBoard[ball.row][ball.col] = null;
        });

        centerRow = Math.floor(centerRow / swipeChain.length);
        centerCol = Math.floor(centerCol / swipeChain.length);

        setConnecting(true);

        // Get accurate position for the points animation
        const centerPos = getBallPosition(centerRow, centerCol);
        if (centerPos) {
          // Add a new points animation
          setPointsAnimations((prev) => [
            ...prev,
            {
              id: Date.now(),
              points: points,
              x: centerPos.centerX,
              y: centerPos.centerY,
            },
          ]);
        }

        // First update the visual board to show empty spaces
        setVisualBoard(newVisualBoard);

        // Then update the logical board
        setBoard(newBoard);
        setAnimatingPop([]);

        // Clear selection state
        setSelected(null);
        setHighlightPath([]);
        setSwipeChain([]);

        // Increment combo if the last move was within 2 seconds
        const newComboCount = comboCount + 1;
        setComboCount(newComboCount);
        if (newComboCount > 1) {
          setShowCombo(true);
          setTimeout(() => setShowCombo(false), 1500);
          // Bonus points for combos
          const bonusPoints = Math.min(newComboCount - 1, 5) * 5;
          points += bonusPoints;

          // Play combo sound
          playSound("combo");

          // Show bonus points
          const comboPos = getBallPosition(centerRow, centerCol);
          if (comboPos) {
            // Use random offset for visual interest
            const offsetX = Math.random() * 10 - 5; // -5 to +5 pixels
            const offsetY = Math.random() * 6 - 15; // -15 to -9 pixels to appear above

            // Add bonus points animation
            setPointsAnimations((prev) => [
              ...prev,
              {
                id: Date.now() + Math.random(),
                points: bonusPoints,
                x: comboPos.centerX + offsetX,
                y: comboPos.centerY + offsetY,
                isBonus: true,
              },
            ]);
          }
        }

        setScore(score + points);
        setMoves(moves - 1);

        // Check for game over
        if (moves <= 1) {
          setTimeout(() => {
            setGameOver(true);
            playSound("gameOver");
          }, 800);
        }

        // After a short delay, begin the falling animation
        setTimeout(() => {
          setAnimatingFall(true);
          applyGravityWithAnimation();
        }, 200);
      }, 400); // Show pop animation for 400ms
    } else {
      // Clear the chain if it wasn't processed
      setSwipeChain([]);
      setHighlightPath([]);
      setSelected(null); // Ensure selection is cleared
    }
  };

  // Handle drag leave - cancel if dragged outside the board
  const handleDragLeave = (e) => {
    if (!isDragging) return;
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Only cancel if we're actually dragging out of the board
    setIsDragging(false);
    setSwipeChain([]);
    setHighlightPath([]);
  };

  // Apply gravity with animation, calculating falling trajectories
  const applyGravityWithAnimation = () => {
    const logicalBoard = [...board];
    const newVisualBoard = structuredClone(visualBoard);

    // Track animation info for each ball that needs to move
    const animations = [];

    // First, calculate all the movements
    for (let col = 0; col < COLS; col++) {
      let emptySpaces = 0;

      // Count empty spaces from bottom up
      for (let row = ROWS - 1; row >= 0; row--) {
        if (logicalBoard[row][col] === null) {
          emptySpaces++;
        } else if (emptySpaces > 0) {
          // This ball needs to fall
          const fromRow = row;
          const toRow = row + emptySpaces;

          // Update the logical board
          logicalBoard[toRow][col] = {
            ...logicalBoard[fromRow][col],
            row: toRow,
          };
          logicalBoard[fromRow][col] = null;

          // Store animation info
          animations.push({
            fromRow,
            toRow,
            col,
            ball: { ...logicalBoard[toRow][col] },
          });
        }
      }

      // Create new balls for the empty spaces at the top
      for (let row = emptySpaces - 1; row >= 0; row--) {
        const newBall = {
          color: COLORS[Math.floor(Math.random() * COLORS.length)],
          row,
          col,
          id: `${row}-${col}-${Math.random().toString(36).substr(2, 5)}`,
        };

        // Set the final position in the logical board
        logicalBoard[row][col] = newBall;

        // Set visual starting position above the board
        animations.push({
          fromRow: -1 * (emptySpaces - row),
          toRow: row,
          col,
          ball: newBall,
          isNew: true,
        });
      }
    }

    // Create a temporary visual board to show the animations
    // First, place balls that don't move
    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col < COLS; col++) {
        const ball = logicalBoard[row][col];
        const shouldAnimate = animations.some(
          (anim) => anim.toRow === row && anim.col === col
        );

        if (!shouldAnimate && ball) {
          newVisualBoard[row][col] = ball;
        }
      }
    }

    // Then apply animations (setting visual positions different from logical ones)
    animations.forEach((anim) => {
      const visualBall = { ...anim.ball };

      // Set the visual position to the starting position
      visualBall.visualRow = anim.fromRow;
      visualBall.targetRow = anim.toRow;
      visualBall.isAnimating = true;

      // Place the ball in its final logical position in the visual board
      // but with properties to animate it from its original position
      newVisualBoard[anim.toRow][anim.col] = visualBall;
    });

    // Update the visual board to start animations
    setVisualBoard(newVisualBoard);

    // Clear any selection state to prevent incorrect highlighting
    setSelected(null);
    setHighlightPath([]);

    // After all animations complete, update the logical board
    setTimeout(() => {
      setBoard(logicalBoard);
      setConnecting(false);
      setAnimatingFall(false);
      setSwipeChain([]); // Clear chain to prevent incorrect selections
      setIsDragging(false); // Reset dragging state
      ballPositions.current = new Map(); // Reset positions after animations

      // Reset combo counter after 2 seconds of no moves
      const comboTimer = setTimeout(() => {
        setComboCount(0);
      }, 2000);

      return () => clearTimeout(comboTimer);
    }, 500); // Animation duration for falling
  };

  // Apply gravity to make balls fall down - logical version
  const applyGravity = () => {
    const newBoard = [...board];

    // For each column, move balls down to fill empty spaces
    for (let col = 0; col < COLS; col++) {
      let emptyRow = -1;

      // Find and fill empty spaces from bottom to top
      for (let row = ROWS - 1; row >= 0; row--) {
        if (newBoard[row][col] === null && emptyRow === -1) {
          emptyRow = row;
        } else if (newBoard[row][col] !== null && emptyRow !== -1) {
          // Move ball down to the empty space
          newBoard[emptyRow][col] = { ...newBoard[row][col], row: emptyRow };
          newBoard[row][col] = null;

          // Look for the next empty space from the current empty row
          for (let r = emptyRow - 1; r >= 0; r--) {
            if (newBoard[r][col] === null) {
              emptyRow = r;
              break;
            }
          }
        }
      }
    }

    // Fill empty spaces at the top with new balls
    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col < COLS; col++) {
        if (newBoard[row][col] === null) {
          newBoard[row][col] = {
            color: COLORS[Math.floor(Math.random() * COLORS.length)],
            row,
            col,
            id: `${row}-${col}-${Math.random().toString(36).substr(2, 5)}`,
          };
        }
      }
    }

    setBoard(newBoard);
  };

  // Check if a ball is in the highlight path
  const isHighlighted = (row, col) => {
    return highlightPath.some((pos) => pos.row === row && pos.col === col);
  };

  // Get the position in the highlight path (for styling)
  const getPathPosition = (row, col) => {
    const index = highlightPath.findIndex(
      (pos) => pos.row === row && pos.col === col
    );
    return index;
  };

  // Check if a ball is being popped (for animation)
  const isPopping = (row, col) => {
    return animatingPop.some((pos) => pos.row === row && pos.col === col);
  };

  // Get ball style including animation
  const getBallStyle = (ball) => {
    if (!ball) return {};

    // Base style
    const style = {
      background: ball.color.gradient,
      boxShadow:
        "inset 0 2px 4px rgba(255,255,255,0.5), 0 2px 4px rgba(0,0,0,0.3)",
    };

    // Add animation for falling balls
    if (ball.isAnimating && ball.visualRow !== undefined) {
      const startY = ball.visualRow * 48; // 48px per ball (40px + 8px margin)
      const endY = ball.targetRow * 48;
      const distance = endY - startY;

      style.transform = "translateY(0)";
      style.animation = `fall-${ball.targetRow}-${ball.col} 0.5s ease-in forwards`;
    }

    return style;
  };

  // Generate keyframes for animations (one for each ball position)
  const generateKeyframes = () => {
    if (!visualBoard.length) return null;

    const keyframes = [];
    visualBoard.forEach((row, rowIndex) => {
      row.forEach((ball, colIndex) => {
        if (ball && ball.isAnimating && ball.visualRow !== undefined) {
          const startY = (ball.visualRow - ball.targetRow) * 48; // 48px difference
          keyframes.push(`
 @keyframes fall-${ball.targetRow}-${ball.col} {
 0% { transform: translateY(${startY}px); }
 100% { transform: translateY(0); }
 }
 `);
        }
      });
    });

    return <style>{keyframes.join("\n")}</style>;
  };

  // Generate SVG path for connection lines
  const generateConnectionPath = () => {
    if (highlightPath.length < 2) return "";

    return highlightPath
      .map((pos, i) => {
        const position = getBallPosition(pos.row, pos.col);
        if (!position) {
          // Fallback to grid-based calculation if exact position not available
          const x = pos.col * 48 + 24;
          const y = pos.row * 48 + 24;
          return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
        }

        return i === 0
          ? `M ${position.centerX} ${position.centerY}`
          : `L ${position.centerX} ${position.centerY}`;
      })
      .join(" ");
  };

  // Play sound effect
  const playSound = (soundName) => {
    if (soundEffects[soundName]) {
      soundEffects[soundName].play();
    }
  };

  // Prevent default touch events
  const preventTouchMove = (e) => {
    if (isDragging) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // Prevent context menu
  const preventContextMenu = (e) => {
    e.preventDefault();
    return false;
  };

  // Function to add floating points with improved variation
  const addFloatingPoints = (centerRow, centerCol, points) => {
    // Get accurate position for the points animation
    const centerPos = getBallPosition(centerRow, centerCol);
    if (centerPos) {
      // Add slight randomness to starting position for visual variety
      const offsetX = Math.random() * 10 - 5; // -5 to +5 pixels
      const offsetY = Math.random() * 10 - 5; // -5 to +5 pixels

      // Add a new points animation
      setPointsAnimations((prev) => [
        ...prev,
        {
          id: Date.now() + Math.random(),
          points: points,
          x: centerPos.centerX + offsetX,
          y: centerPos.centerY + offsetY,
        },
      ]);
    }
  };

  // Store ball element position for accurate path drawing
  const registerBallPosition = (ball, element) => {
    if (ball && element) {
      const rect = element.getBoundingClientRect();
      const boardRect = gameBoardRef.current?.getBoundingClientRect();

      if (boardRect) {
        // Store position relative to the game board
        ballPositions.current.set(`${ball.row}-${ball.col}`, {
          centerX: rect.left + rect.width / 2 - boardRect.left,
          centerY: rect.top + rect.height / 2 - boardRect.top,
        });
      }
    }
  };

  // Get registered ball position
  const getBallPosition = (row, col) => {
    return ballPositions.current.get(`${row}-${col}`);
  };

  // Reset the game to initial state
  const resetGame = () => {
    const newBoard = [];
    for (let row = 0; row < ROWS; row++) {
      const newRow = [];
      for (let col = 0; col < COLS; col++) {
        newRow.push({
          color: COLORS[Math.floor(Math.random() * COLORS.length)],
          row,
          col,
          id: `${row}-${col}-${Math.random().toString(36).substr(2, 5)}`,
        });
      }
      newBoard.push(newRow);
    }
    setBoard(newBoard);
    setVisualBoard(structuredClone(newBoard));
    setSelected(null);
    setScore(0);
    setGameOver(false);
    setIsGameOver(false);
    setMoves(20);
    setComboCount(0);
    setShowCombo(false);
    setSwipeChain([]);
    setHighlightPath([]);
    setAnimatingFall(false);
    setAnimatingPop([]);
    setPointsAnimations([]);
    ballPositions.current = new Map();
    setIntroTextFading(false);

    // Don't show intro text on reset - only when user first enters game
    // setShowIntroText(true); // Removed this line
  };

  // Check if two balls are adjacent
  const areAdjacent = (ball1, ball2) => {
    if (!ball1 || !ball2) return false;

    const rowDiff = Math.abs(ball1.row - ball2.row);
    const colDiff = Math.abs(ball1.col - ball2.col);

    // Balls are adjacent if they're one step away horizontally or vertically
    return (rowDiff === 1 && colDiff === 0) || (rowDiff === 0 && colDiff === 1);
  };

  // Check if ball is already in chain
  const isInChain = (ball) => {
    return swipeChain.some(
      (item) => item.row === ball.row && item.col === ball.col
    );
  };

  // Check if ball can be added to chain (same color and adjacent to last ball)
  const canAddToChain = (ball) => {
    if (!ball || swipeChain.length === 0) return false;

    const lastBall = swipeChain[swipeChain.length - 1];
    return (
      ball.color.name === lastBall.color.name &&
      areAdjacent(ball, lastBall) &&
      !isInChain(ball)
    );
  };

  // Function to reset the intro seen flag (for testing purposes only)
  const resetIntroFlag = () => {
    sessionStorage.removeItem("rainbowIntroSeen");
    setShowIntroText(true);
    setIntroTextFading(false);
  };

  return (
    <>
      <div
        className="menupointer game-container4"
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          pointerEvents: "fill",
          touchAction: "none", // Disable browser handling of all touch gestures
        }}
        onContextMenu={preventContextMenu}
      >
        <div
          ref={gameContainerRef}
          style={{ pointerEvents: "fill", height: "100vh" }}
          className="container d-flex flex-column align-items-center justify-content-center p-2 rainbow-background  mx-auto text-white"
          onTouchMove={preventTouchMove}
          onContextMenu={preventContextMenu}
        >
          {generateKeyframes()}

          <div className="game-header mb-4 text-center">
            <h2 className=" welcometextmeme">Rainbow Connect</h2>
            <img
              onClick={() => {
                toogleMenu(task, "task");
              }}
              src="https://d1ccopf5cw1hft.cloudfront.net/s3data/cancelicon.svg"
              className="cancel-full"
              style={{ cursor: "pointer" }}
            />
            <button
              className="btn btn-outline-light help-btn rounded-circle"
              onClick={() => setShowInstructionsModal(true)}
            >
              <i className="bi bi-question-circle"></i>
            </button>
          </div>

          <div className="w-100 mb-4 d-flex justify-content-between align-items-center position-relative">
            <div className="w-100 score-box p-2 rounded d-flex flex-column align-items-center">
              <span className="small text-info fw-bold">SCORE:{score}</span>
            </div>

            {showCombo && comboCount > 1 && (
              <div className="combo-text-container">
                <div className="combo-text">{comboCount}x COMBO!</div>
              </div>
            )}

            <div className="w-100 moves-box p-2 rounded d-flex flex-column align-items-center">
              <span className="small text-danger fw-bold">MOVES:{moves}</span>
            </div>
          </div>

          <div
            ref={gameBoardRef}
            className="position-relative game-boardrainbow p-2 rounded"
            onClick={dismissIntroText}
            onTouchStart={dismissIntroText}
            onMouseUp={handleBallUp}
            onMouseLeave={handleDragLeave}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleBallUp(e);
            }}
            onTouchCancel={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDragLeave(e);
            }}
            onTouchMove={(e) => {
              if (isDragging) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            {/* Intro text overlay with click-to-dismiss */}
            {showIntroText && (
              <div
                className={`intro-text-overlay ${
                  introTextFading ? "fade-out" : ""
                }`}
                onClick={handleHideIntroText}
              >
                <div className="intro-text">
                  <div className="intro-text-title text-uppercase">
                    Connect the Rainbow!
                  </div>

                  <div className="intro-text-instructions">
                    Tap or swipe on a ball to begin
                  </div>
                </div>
              </div>
            )}

            {/* Points animations */}
            {pointsAnimations.map((anim) => (
              <div
                key={anim.id}
                className={`points-animation ${
                  anim.isBonus ? "bonus-points" : ""
                }`}
                style={{
                  left: `${anim.x}px`,
                  top: `${anim.y}px`,
                }}
              >
                +{anim.points}
              </div>
            ))}

            {/* Connection line for swipe path */}
            {highlightPath.length > 1 && (
              <svg
                className="position-absolute top-0 start-0 w-100 h-100"
                style={{ padding: "0", pointerEvents: "none", zIndex: 10 }}
              >
                <path
                  d={generateConnectionPath()}
                  stroke={
                    swipeChain.length > 0 ? swipeChain[0].color.name : "white"
                  }
                  strokeWidth="6"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeOpacity="0.6"
                  strokeDasharray="5,5"
                />
              </svg>
            )}

            {/* Game board */}
            {visualBoard.map((row, rowIndex) => (
              <div key={rowIndex} className="d-flex">
                {row.map((ball, colIndex) => (
                  <div
                    key={ball ? ball.id : `empty-${rowIndex}-${colIndex}`}
                    className={`game-ball ${
                      isHighlighted(rowIndex, colIndex) ? `highlighted` : ""
                    } ${isPopping(rowIndex, colIndex) ? "popping" : ""}`}
                    style={getBallStyle(ball)}
                    onMouseDown={(e) =>
                      ball && handleBallDown(ball, e, e.currentTarget)
                    }
                    onMouseOver={(e) =>
                      ball && handleBallMove(ball, e, e.currentTarget)
                    }
                    onTouchStart={(e) =>
                      ball && handleBallDown(ball, e, e.currentTarget)
                    }
                    onTouchMove={(e) => {
                      if (!ball || !isDragging) return;
                      e.preventDefault();
                      e.stopPropagation();

                      // Get touch position and find the element at that position
                      const touch = e.touches[0];
                      const element = document.elementFromPoint(
                        touch.clientX,
                        touch.clientY
                      );

                      // Find ball coordinates from the element's data attributes
                      if (element && element.dataset) {
                        const row = parseInt(element.dataset.row);
                        const col = parseInt(element.dataset.col);
                        if (
                          !isNaN(row) &&
                          !isNaN(col) &&
                          board[row] &&
                          board[row][col]
                        ) {
                          handleBallMove(board[row][col], e, element);
                        }
                      }
                    }}
                    data-row={rowIndex}
                    data-col={colIndex}
                    ref={(el) => {
                      if (ball && el) {
                        // Store ball position when it renders
                        registerBallPosition(ball, el);
                      }
                    }}
                  >
                    {ball && <div className="ball-highlight" />}
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* {gameOver && !IsGameOver && (
 <div className="mt-4 p-4 game-over-modal rounded text-center w-100">
 <h2 className="fs-3 fw-bold text-warning mb-2">Game Over!</h2>
 <p className="mb-4 fs-5">
 Final Score: <span className="fw-bold text-white">{score}</span>
 </p>
 <button
 className="btn btn-success btn-lg rounded-pill"
 onClick={resetGame}
 >
 Play Again
 </button>
 </div>
 )} */}

          {!gameOver && !IsGameOver && (
            <div className="mt-4 d-flex justify-content-center gap-3">
              {/* <button
 className="btn btn-primary btn-lg rounded-pill"
 onClick={resetGame}
 >
 New Game
 </button> */}
            </div>
          )}

          {/* Game Over Popup like MemeCandy.js */}
          {IsGameOver && (
            <div className="overlay">
              <div className="rainbow-full-popup">
                <div className="rainbow-popup-content">
                  <h2 className="quento-epic">Game Over!</h2>
                  <img
                    src="https://d1ccopf5cw1hft.cloudfront.net/s3data/Xicon.png"
                    className="quento-cancel-img"
                    onClick={() => {
                      playClick();
                      closePopUp();
                      resetGame();
                    }}
                  />
                  {!IsPointsReached ? (
                    <div className="row text-center">
                      <div className="col-12">
                        <div
                          className="rainbow-epic-div"
                          style={{ padding: "30px 40px" }}
                        >
                          <p className="quento-pointsLabel">TOKENS EARNED</p>
                          <div className="quento-pointsValue1">
                            <img
                              src="https://d1ccopf5cw1hft.cloudfront.net/s3data/coinlogo.png"
                              alt="logo"
                            />
                            <p className="quento-pointsNumber">{score}</p>
                          </div>
                        </div>
                        <p className="quento-bottom-text">
                          Keep connecting rainbow balls! 🌈 Challenge yourself
                          to beat your high score!
                        </p>
                        <button
                          className="control-btn"
                          onClick={() => {
                            playClick();
                            resetGame();
                          }}
                        >
                          Play Again
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="row text-center">
                      <div className="col-12">
                        <div className="quento-epic-div">
                          <p
                            className="quento-pointsLabel"
                            style={{
                              fontSize: "18px",
                              fontWeight: "bold",
                              margin: "20px 0",
                              textAlign: "center",
                            }}
                          >
                            POINTS EARNED: {score} <br />
                            TOKENS EARNED: NIL
                          </p>
                        </div>
                        <p className="quento-bottom-text text-uppercase">
                          Great job! 🌈 You've played your limit for today —
                          come back tomorrow for more!
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Instructions Modal */}
          {showInstructionsModal && (
            <div
              className="instructions-modal-backdrop"
              onClick={() => setShowInstructionsModal(false)}
            >
              <div
                className="instructions-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="instructions-modal-header">
                  <h3
                    className="welcometextmeme"
                    style={{ marginBottom: "0px" }}
                  >
                    How to Play
                  </h3>
                  <div
                    className="btn-close btn-close-white"
                    onClick={() => setShowInstructionsModal(false)}
                    aria-label="Close"
                  >
                    <img
                      style={{ width: "24px", height: "24px" }}
                      src="https://d1ccopf5cw1hft.cloudfront.net/s3data/Xicon.png"
                      alt="Close"
                    />
                  </div>
                </div>

                <div className="instructions-modal-body">
                  <div className="instruction-item">
                    <div className="instruction-icon">
                      <i className="bi bi-arrow-left-right"></i>
                    </div>
                    <div className="instruction-text">
                      Swipe to connect matching colored balls in any direction
                    </div>
                  </div>

                  <div className="instruction-item">
                    <div className="instruction-icon">
                      <i className="bi bi-link-45deg"></i>
                    </div>
                    <div className="instruction-text">
                      Connect longer chains for more points!
                    </div>
                  </div>

                  <div className="instruction-item">
                    <div className="instruction-icon">
                      <i className="bi bi-stars"></i>
                    </div>
                    <div className="instruction-text">
                      Make combos for bonus points
                    </div>
                  </div>

                  <div className="instruction-item">
                    <div className="instruction-icon">
                      <i className="bi bi-controller"></i>
                    </div>
                    <div className="instruction-text">
                      You have 20 moves to get the highest score
                    </div>
                  </div>
                  {/* <div className="instruction-example">
 <div className="example-title">Example:</div>
 <div className="example-balls">
 {["red", "red", "red"].map((color, i) => (
 <div
 key={i}
 className="example-ball"
 style={{
 background: COLORS.find((c) => c.name === color)
 .gradient,
 ...(i === 1
 ? { marginLeft: "8px", marginRight: "8px" }
 : {}),
 }}
 ></div>
 ))}
 </div>
 <div className="example-points">3 balls = 15 points</div>
 </div> */}
                </div>

                <div className="instructions-modal-footer">
                  <button
                    className="control_btn_rainbow"
                    onClick={() => setShowInstructionsModal(false)}
                  >
                    Got it!
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
